import { Colors } from "~foundations";
import { RadioInput, type RadioInputProps } from "~atoms";

/**
 * Represents a single radio option within a RadioList.
 *
 * Inherits all props from RadioInputProps except the fields that
 * are managed and injected by the RadioList itself.
 *
 * - `value` is redefined to use the generic TValue, ensuring strong typing.
 */
type RadioOption<TValue extends string | number> = Omit<
  RadioInputProps<TValue>,
  "groupName" | "onChange" | "dataTestId" | "value" | "isSelected"
> & {
  value: TValue;
};

type RadioListProps<TValue extends string | number> = {
  /**
   * Array of radio options to display
   */
  options: ReadonlyArray<RadioOption<TValue>>;
  /**
   * The value of the currently selected radio input.
   * Set to `undefined` when no option is selected.
   * @default undefined
   */
  value?: TValue;
  /**
   * Callback when selection changes
   */
  onChange: (value: TValue | undefined) => void;
  /**
   * Shared interaction status for all inputs in the list.
   * Can be overridden by individual options.
   * @default "enabled"
   */
  status?: "enabled" | "readOnly" | "disabled";
  /**
   * Shared name for all radios in this list.
   * Passed as the native `name` attribute to each radio input.
   * @default undefined
   */
  groupName?: string;
  /**
   * Optional test ID prefix used to generate test IDs for each input.
   * The actual ID will be `${dataTestId}-${value}`.
   * @default undefined
   */
  "data-testid"?: string;
};

/**
 * Component | RadioList
 *
 * A list of radio inputs with consistent styling and behavior.
 * Automatically handles bottom borders for all inputs except the last one.
 */
export function RadioList<TValue extends string | number>(props: RadioListProps<TValue>) {
  const {
    groupName,
    options,
    value,
    onChange,
    "data-testid": dataTestId,
    status = "enabled",
  } = props;

  return (
    <div
      css={{
        "& > *:not(:last-child)": {
          borderBottom: `1px solid ${Colors.slate200}`,
        },
      }}
    >
      {options.map((option) => (
        <RadioInput
          key={`${groupName}-${option.value}`}
          {...option}
          groupName={groupName}
          isSelected={option.value === value}
          status={status ?? option.status}
          onChange={() => onChange(option.value)}
          data-testid={dataTestId}
        />
      ))}
    </div>
  );
}
